  // src/belediye/pages/saha/SahaPersoneliAssignmentDetailPage.js
  import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";

  import { useDispatch, useSelector } from "react-redux";
  import { useNavigate, useParams } from "react-router-dom";

  import {
    fetchAssignmentParticipantDetail,
    submitFeedback,
    clearFeedbackStatus,
    clearDetail,
  } from "../../redux/slices/sahaAssignmentsSlice";

  import BasicModal from "../../components/ui/BasicModal";
  import { downloadFile } from "../../components/utils/downloadFile";

  import {
    ChevronUpDownIcon,
    UserIcon,
    IdentificationIcon,
    DocumentTextIcon,
    MapPinIcon,
    PaperClipIcon,
    PhotoIcon,
    ArrowDownTrayIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ClockIcon,
    ShieldCheckIcon,
    WrenchScrewdriverIcon,
    Square2StackIcon,
    XMarkIcon,
  } from "@heroicons/react/24/outline";

  import TrDateTime from "../../components/utils/TrDateTime";
  // FAZ-88: detay başlığını navbara bildir
  import { useNavbarBasligiAyarla } from "../../components/NavbarBaslikContext";
  import {
  safeText,
  formatPersonNameTr,
  titleCaseTr,
  ensureFirstUpperTr,
} from "../../components/utils/textFormat";



  import "./SahaPersoneliAssignmentDetailPage.css";
  import "./SahaPersoneliAssignmentDetailPage.mobile.css";

  const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
  const STATUS_LABELS = { success: "Başarılı", failure: "Başarısız", pending: "Beklemede" };

  

  function guessFileType(url) {
    if (!url) return "unknown";
    const u = String(url).toLowerCase();
    if (u.endsWith(".pdf")) return "pdf";
    if (u.startsWith("blob:")) return "image";
    if (u.endsWith(".png") || u.endsWith(".jpg") || u.endsWith(".jpeg") || u.includes("image")) return "image";
    return "unknown";
  }

  function getFileBase() {
    const env =
      process.env.REACT_APP_FILE_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      "";

    if (env) {
      try {
        return new URL(env, window.location.origin).origin.replace(/\/+$/, "");
      } catch {
        return String(env).replace(/\/+$/, "").replace(/\/api\/?$/, "");
      }
    }
    return window.location.origin.replace(/\/+$/, "");
  }

  function toAbsoluteUrl(maybeUrl) {
    if (!maybeUrl) return "";
    const u = String(maybeUrl).trim();
    if (!u) return "";
    if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("blob:")) return u;

    const base = getFileBase();
    if (u.startsWith("/")) return `${base}${u}`;
    return `${base}/${u}`;
  }

  function normalizeUrlList(list) {
    if (!Array.isArray(list)) return [];
    return list.map(toAbsoluteUrl).filter(Boolean);
  }

  function getUnitName(app = {}) {
    return (
      app?.unit_name ||
      app?.unit_detail?.name ||
      app?.unit_detail?.title ||
      app?.unitDetail?.name ||
      (typeof app?.unit === "object" ? app?.unit?.name || app?.unit?.title : null) ||
      null
    );
  }

  function getNeighborhoodName(app = {}) {
    return (
      app?.neighborhood_name ||
      app?.neighborhood_detail?.name ||
      app?.neighborhoodDetail?.name ||
      (typeof app?.neighborhood === "object" ? app?.neighborhood?.name : null) ||
      null
    );
  }

  function KeyValueList({ items }) {
    return (
      <dl className="saha-kv">
        {items.map((it) => (
          <div key={it.key} className="saha-kv-row">
            <dt className="saha-kv-key">{it.key}</dt>
            <dd className="saha-kv-val">{it.value}</dd>
          </div>
        ))}
      </dl>
    );
  }

  function CollapsibleCard({ icon: Icon, title, open, onToggle, children }) {
    return (
      <section className="md-card saha-acc">
        <button type="button" className="saha-acc-head" onClick={onToggle} aria-expanded={open ? "true" : "false"}>
          <div className="saha-acc-head-left">
            {Icon ? <Icon className="saha-acc-icon" /> : null}
            <div className="saha-acc-head-text">
              <div className="saha-acc-title">{title}</div>
            </div>
          </div>

          <ChevronUpDownIcon className={`saha-acc-chevron ${open ? "" : "is-collapsed"}`} />
        </button>

        {open ? <div className="saha-acc-body">{children}</div> : null}
      </section>
    );
  }
  function normalizePhoneForCopy(raw) {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";

    // 10 hane: 5555555555 -> 05555555555
    if (digits.length === 10) return `0${digits}`;

    // 11 hane ve 0 ile başlıyorsa: 0555...
    if (digits.length === 11 && digits.startsWith("0")) return digits;

    // 90 ile gelirse: 90555... -> 0555...
    if (digits.length === 12 && digits.startsWith("90")) return `0${digits.slice(2)}`;

    return digits;
  }

  const PhoneCopyValue = React.memo(function PhoneCopyValue({ phoneDisplay, phoneRaw }) {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
      return () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
      };
    }, []);

    const onCopy = useCallback(async () => {
      const phoneToCopy = normalizePhoneForCopy(phoneRaw || phoneDisplay);
      if (!phoneToCopy) return;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(phoneToCopy);
        } else {
          const ta = document.createElement("textarea");
          ta.value = phoneToCopy;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }

        setCopied(true);
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setCopied(false), 1500);
      } catch {
        // istersen burada hata toast'ı gösterebilirsin
      }
    }, [phoneRaw, phoneDisplay]);

    if (!phoneDisplay || phoneDisplay === "—") return <span>{phoneDisplay || "—"}</span>;

    return (
      <span className="saha-phone">
        <span className="saha-phone-text">{phoneDisplay}</span>

        <button
          type="button"
          className="btn-md btn-md-ghost saha-copy-btn"
          onClick={onCopy}
          title="Telefonu kopyala"
          aria-label="Telefonu kopyala"
        >
          <Square2StackIcon width={18} height={18} />
        </button>

        {copied ? <span className="saha-copied">Kopyalandı</span> : null}
      </span>
    );
  });


  const SahaPersoneliAssignmentDetailPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { participantId } = useParams();

    const { detail, detailLoading, detailError, feedbackStatus, feedbackError } = useSelector(
      (state) => state.sahaAssignments
    );

    const [fbStatus, setFbStatus] = useState("");
    const [fbNote, setFbNote] = useState("");
    const [fbFiles, setFbFiles] = useState([]);
    const [fbError, setFbError] = useState("");

    const [openMap, setOpenMap] = useState({
      contact: true,
      app: true,
      attachments: true,
      location: true,
      assignment: true,
    });
    const toggle = (key) => setOpenMap((p) => ({ ...p, [key]: !p[key] }));

    const [previewUrls, setPreviewUrls] = useState([]);
    const previewRef = useRef([]);

    useEffect(() => {
      previewRef.current.forEach((u) => URL.revokeObjectURL(u));
      previewRef.current = [];

      const urls = fbFiles.map((f) => {
        const u = URL.createObjectURL(f);
        previewRef.current.push(u);
        return u;
      });
      setPreviewUrls(urls);

      return () => {
        previewRef.current.forEach((u) => URL.revokeObjectURL(u));
        previewRef.current = [];
      };
    }, [fbFiles]);

    const [mediaModal, setMediaModal] = useState({ open: false, url: "", type: "image" });
    const [confirmModal, setConfirmModal] = useState({ open: false });


    const applicationDetail = detail?.application_detail; // undefined olabilir, sorun değil
  const feedbacksDep = detail?.feedbacks;

  /* FAZ-88 — saha kabuğunda sidebar yok; dar katmanda sol yuva boş kalır ama
     TALEP DETAYINDA geri okuna döner ve orta yuva başvurunun konusunu yazar. */
  useNavbarBasligiAyarla(
    applicationDetail
      ? {
          ad: applicationDetail.subject || "Talep",
          no: applicationDetail.tracking_number || "",
        }
      : null,
  );

  const unitName = useMemo(() => getUnitName(applicationDetail || {}), [applicationDetail]);
  const neighborhoodName = useMemo(() => getNeighborhoodName(applicationDetail || {}), [applicationDetail]);

  const images = useMemo(() => normalizeUrlList((applicationDetail || {})?.files), [applicationDetail]);
  const documents = useMemo(() => normalizeUrlList((applicationDetail || {})?.documents), [applicationDetail]);


    const latestFeedback = useMemo(() => {
      if (!Array.isArray(feedbacksDep) || !feedbacksDep.length) return null;
      const fb = feedbacksDep[feedbacksDep.length - 1];
      return { ...fb, file_urls: normalizeUrlList(fb?.file_urls) };
    }, [feedbacksDep]);

    const feedbackAlreadySent = detail?.has_feedback === true;

    useEffect(() => {
      if (!participantId) return;

      dispatch(fetchAssignmentParticipantDetail(participantId));
      return () => {
        dispatch(clearDetail());
        dispatch(clearFeedbackStatus());
      };
    }, [dispatch, participantId]);

    const handleFileChange = (e) => {
      const picked = Array.from(e.target.files || []);
      const filtered = picked.filter((f) => ALLOWED_IMAGE_TYPES.includes(f.type));

      

      setFbError("");
      setFbFiles((prev) => [...prev, ...filtered]);
      e.target.value = "";
    };

    const removeFile = (index) => setFbFiles((prev) => prev.filter((_, i) => i !== index));

    const openMedia = (url, explicitType) => {
      const abs = toAbsoluteUrl(url);
      const type = explicitType || guessFileType(abs);
      setMediaModal({ open: true, url: abs, type });
    };
    const closeMedia = () => setMediaModal({ open: false, url: "", type: "image" });

    const requestSubmit = (e) => {
      e.preventDefault();

      if (!detail?.id) return setFbError("Detay verisi bulunamadı.");
      if (!fbStatus) return setFbError("Lütfen durum seçin.");
      if (!fbNote.trim()) return setFbError("Lütfen açıklama girin.");
      

      setFbError("");
      setConfirmModal({ open: true });
    };

    const confirmSubmit = () => {
      setConfirmModal({ open: false });
      dispatch(
        submitFeedback({
          participant: detail.id,
          status: fbStatus,
          note: fbNote,
          files: fbFiles,
        })
      );
    };

    if (detailLoading) {
      return (
        <div className="page-container" style={{ paddingTop: 12, paddingBottom: 24 }}>
          <div className="md-card saha-detail-state">
            <ClockIcon width={18} height={18} />
            <div>Yükleniyor…</div>
          </div>
        </div>
      );
    }

    if (detailError) {
      return (
        <div className="page-container" style={{ paddingTop: 12, paddingBottom: 24 }}>
          <div className="md-card saha-detail-state saha-detail-state--error">
            <ExclamationTriangleIcon width={18} height={18} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 650 }}>Hata</div>
              <div style={{ color: "var(--color-text-soft)" }}>{detailError}</div>
            </div>
            <button type="button" className="btn-md btn-md-ghost" onClick={() => navigate(-1)}>
              Geri dön
            </button>
          </div>
        </div>
      );
    }

    if (!detail) {
      return (
        <div className="page-container" style={{ paddingTop: 12, paddingBottom: 24 }}>
          <div className="md-card saha-detail-state">
            <ExclamationTriangleIcon width={18} height={18} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 650 }}>Kayıt bulunamadı</div>
              <div style={{ color: "var(--color-text-soft)" }}>Bu talep kaldırılmış olabilir.</div>
            </div>
            <button type="button" className="btn-md btn-md-ghost" onClick={() => navigate(-1)}>
              Geri dön
            </button>
          </div>
        </div>
      );
    }

    const { is_responder, has_feedback, assignment_note, user_detail = {}, added_at, assignment } = detail;

    const { subject, description, address, latitude, longitude, created_at, tracking_number, citizen } = applicationDetail || {};
    const assignedAt = added_at;
  //   Citizen bilgileri (Ad Soyad + Telefon)
  const c = citizen || {};
 const citizenFullName = titleCaseTr(
  ([c?.name, c?.surname].filter(Boolean).join(" ").trim()) ||
  ([c?.first_name, c?.last_name].filter(Boolean).join(" ").trim())
);


  const citizenPhone = safeText(
    c?.phone_number || c?.phone || c?.gsm || c?.mobile || ""
  );

    const hasLocation = Boolean(latitude && longitude);
    const hasAttachments = Boolean((images && images.length) || (documents && documents.length));
    const canSendFeedback = Boolean(is_responder) && !feedbackAlreadySent;

    const actionsStatusChip = has_feedback ? (
      <span className="md-chip md-chip-success">
        <CheckCircleIcon width={16} height={16} /> Tamamlandı
      </span>
    ) : is_responder ? (
      <span className="md-chip md-chip-warning">
        <ClockIcon width={16} height={16} /> Geri bildirim bekleniyor
      </span>
    ) : (
      <span className="md-chip md-chip-muted">
        <ShieldCheckIcon width={16} height={16} /> Yetki yok
      </span>
    );

    const contactItems = [
    { key: "Başvuru sahibi", value: citizenFullName },

    {
    key: "Telefon Numarası",
    value: <PhoneCopyValue phoneDisplay={citizenPhone} phoneRaw={c?.phone_number || c?.phone || c?.gsm || c?.mobile || ""} />,
  },


    { key: "Takip No", value: safeText(tracking_number) },
  ];



    const appItems = [
      {
  key: "Konu",
  value: <span className="saha-wrap">{ensureFirstUpperTr(subject)}</span>,
},
{
  key: "Açıklama",
  value: <span className="saha-wrap">{ensureFirstUpperTr(description)}</span>,
},

      ...(unitName ? [{ key: "Birim", value: safeText(unitName) }] : []),
      ...(neighborhoodName ? [{ key: "Mahalle", value: safeText(neighborhoodName) }] : []),
     ...(address
  ? [{ key: "Adres", value: <span className="saha-wrap">{ensureFirstUpperTr(address)}</span> }]
  : []),

      ...(created_at ? [{ key: "Başvuru tarihi", value: <TrDateTime value={created_at} /> }] : []),

    ];

    const hasAssignmentInfo = Boolean(assignment || assignedAt || assignment_note || user_detail);
    const assignmentItems = [
      ...(assignment ? [{ key: "Atama ID", value: safeText(assignment) }] : []),
      ...(assignedAt ? [{ key: "Atama zamanı", value: <TrDateTime value={assignedAt} /> }] : []),

      { key: "Atayan kişi", value: formatPersonNameTr(user_detail) },

      ...(assignment_note
  ? [{
      key: "Atama notu",
      value: <span className="saha-wrap">{ensureFirstUpperTr(assignment_note)}</span>,
    }]
  : []),

    ];

    return (
      <div className="page-container saha-detail-page" style={{ paddingTop: 12, paddingBottom: 28 }}>
        <div className="saha-detail-grid">
          <div className="saha-detail-left">
            <CollapsibleCard icon={UserIcon} title="İletişim bilgileri" open={openMap.contact} onToggle={() => toggle("contact")}>
              <KeyValueList items={contactItems} />
            </CollapsibleCard>

            <CollapsibleCard icon={DocumentTextIcon} title="Başvuru detayları" open={openMap.app} onToggle={() => toggle("app")}>
              <KeyValueList items={appItems} />
            </CollapsibleCard>

            {hasAttachments ? (
              <CollapsibleCard icon={PaperClipIcon} title="Ek Dosyalar" open={openMap.attachments} onToggle={() => toggle("attachments")}>
                {images?.length ? (
                  <div className="saha-attachments">
                    <div className="saha-attachments-title">
                      <PhotoIcon width={18} height={18} />
                      Görseller
                    </div>
                    <div className="saha-thumb-grid">
                      {images.map((url, idx) => (
                        <button key={`${url}-${idx}`} type="button" className="saha-thumb" onClick={() => openMedia(url, "image")} title="Görseli büyüt">
                          <img src={url} alt={`Görsel ${idx + 1}`} loading="lazy" />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {documents?.length ? (
                  <div className="saha-docs">
                    <div className="saha-attachments-title" style={{ marginTop: images?.length ? 14 : 0 }}>
                      <DocumentTextIcon width={18} height={18} />
                      Belgeler
                    </div>
                    <div className="saha-doc-grid">
                      {documents.map((url, idx) => (
                        <button key={`${url}-${idx}`} type="button" className="saha-doc-card" onClick={() => openMedia(url, "pdf")} title="Belgeyi aç">
                          <div className="saha-doc-icon">📄</div>
                          <div className="saha-doc-name">Belge {idx + 1}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </CollapsibleCard>
            ) : null}

            {hasLocation ? (
              <CollapsibleCard icon={MapPinIcon} title="Konum" open={openMap.location} onToggle={() => toggle("location")}>
                <div className="saha-location">
                  <div className="saha-map-frame">
                    <iframe
                      title="Harita"
                      src={`https://maps.google.com/maps?q=${latitude},${longitude}&z=16&output=embed`}
                      width="100%"
                      height="240"
                      style={{ border: 0 }}
                      loading="lazy"
                    />
                  </div>

                  <a className="btn-md btn-md-ghost saha-map-cta" href={`https://maps.google.com/maps?daddr=${latitude},${longitude}`} target="_blank" rel="noopener noreferrer">
                    <MapPinIcon width={18} height={18} />
                    Google Maps’te yol tarifi al
                  </a>
                </div>
              </CollapsibleCard>
            ) : null}

            {hasAssignmentInfo ? (
              <CollapsibleCard icon={IdentificationIcon} title="Atama bilgileri" open={openMap.assignment} onToggle={() => toggle("assignment")}>
                <KeyValueList items={assignmentItems} />
              </CollapsibleCard>
            ) : null}
          </div>

          <aside className="saha-detail-right">
            <section className={`md-card saha-actions ${canSendFeedback ? "" : "saha-actions--disabled"}`}>
              <div className="saha-actions-head">
                <div className="saha-actions-title">
                  <WrenchScrewdriverIcon width={18} height={18} />
                  İşlemler
                </div>
                {actionsStatusChip}
              </div>

              <div className="saha-actions-body">
                {feedbackAlreadySent ? (
                  <div>
                    <div className="saha-actions-note">
                      <CheckCircleIcon width={18} height={18} />
                      Bu talep için geri bildirim gönderilmiş.
                    </div>

                    {latestFeedback ? (
                      <div className="saha-feedback-summary">
                        <KeyValueList
                          items={[
                            { key: "Durum", value: STATUS_LABELS[latestFeedback.status] || safeText(latestFeedback.status) },
                            {
  key: "Açıklama",
  value: <span className="saha-wrap">{ensureFirstUpperTr(latestFeedback.note)}</span>,
},

                            { key: "Tarih", value: <TrDateTime value={latestFeedback.created_at} /> },

                          ]}
                        />

                        {Array.isArray(latestFeedback.file_urls) && latestFeedback.file_urls.length ? (
                          <div className="saha-attachments" style={{ marginTop: 12 }}>
                            <div className="saha-attachments-title">
                              <PhotoIcon width={18} height={18} />
                              Yüklenen görseller
                            </div>
                            <div className="saha-thumb-grid">
                              {latestFeedback.file_urls.map((url, idx) => (
                                <button key={`${url}-${idx}`} type="button" className="saha-thumb" onClick={() => openMedia(url, "image")} title="Görseli büyüt">
                                  <img src={url} alt={`Yüklenen ${idx + 1}`} loading="lazy" />
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <form onSubmit={requestSubmit} className="saha-feedback-form2">
                    {!is_responder ? (
                      <div className="saha-actions-note">
                        <ShieldCheckIcon width={18} height={18} />
                        Bu talep için geri bildirim gönderme yetkiniz yok.
                      </div>
                    ) : null}

                    {is_responder ? (
                      <>
                        {(feedbackStatus === "failed" && (feedbackError || fbError)) || fbError ? (
                          <div className="saha-inline-error">
                            <ExclamationTriangleIcon width={18} height={18} />
                            <div className="saha-wrap">{feedbackError || fbError}</div>
                          </div>
                        ) : null}

                        {feedbackStatus === "succeeded" ? (
                          <div className="saha-inline-success">
                            <CheckCircleIcon width={18} height={18} />
                            <div>Geri bildiriminiz gönderildi.</div>
                          </div>
                        ) : null}

                        <div className="saha-field">
                          <label className="saha-label">
                            Durum <span className="saha-req">*</span>
                          </label>
                          <select className="md-select" value={fbStatus} onChange={(e) => setFbStatus(e.target.value)} disabled={feedbackStatus === "loading"} required>
                            <option value="">Seçiniz</option>
                            <option value="success">Başarılı</option>
                            <option value="failure">Başarısız</option>
                          </select>
                        </div>

                        <div className="saha-field">
                          <label className="saha-label">
                            Açıklama <span className="saha-req">*</span>
                          </label>
                          <textarea className="md-textarea" rows={4} value={fbNote} onChange={(e) => setFbNote(e.target.value)} placeholder="Yaptığınız işlem, kullanılan malzeme vb." disabled={feedbackStatus === "loading"} required />
                        </div>

                        <div className="saha-field">
                          <label className="saha-label">Fotoğraf (PNG/JPG)</label>

                          <input type="file" className="md-input" multiple accept="image/png, image/jpeg, image/jpg" onChange={handleFileChange} disabled={feedbackStatus === "loading"} />

                          {previewUrls.length ? (
                            <div className="saha-picked">
                              {previewUrls.map((u, i) => (
                                <div key={u} className="saha-picked-item">
                                  <button type="button" className="saha-picked-thumb" onClick={() => setMediaModal({ open: true, url: u, type: "image" })} title="Önizleme">
                                    <img src={u} alt={`Seçilen ${i + 1}`} />
                                  </button>

                                  <button type="button" className="btn-md btn-md-ghost saha-picked-remove" onClick={() => removeFile(i)} title="Kaldır" aria-label="Kaldır">
                                    <XMarkIcon width={16} height={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <button
    type="submit"
    className="btn-md saha-feedback-submit"
    disabled={feedbackStatus === "loading"}
  >
    {feedbackStatus === "loading" ? "Gönderiliyor…" : "Gönder"}
  </button>


                        <div className="saha-actions-footnote">Gönderim öncesi onay istenir.</div>
                      </>
                    ) : null}
                  </form>
                )}
              </div>
            </section>
          </aside>
        </div>

        {/* Media modal (ortak BasicModal) */}
        <BasicModal
          open={mediaModal.open}
          title={mediaModal.type === "pdf" ? "Belge" : "Görsel"}
          onClose={closeMedia}
          actions={
            mediaModal.url ? (
              <>
                <a className="btn-md btn-md-ghost" href={mediaModal.url} target="_blank" rel="noopener noreferrer">
                  <DocumentTextIcon width={18} height={18} />
                  Yeni sekmede aç
                </a>

                <button
                  type="button"
                  className="btn-md btn-md-primary"
                  onClick={() =>
                    downloadFile(mediaModal.url, {
                      token: localStorage.getItem("accessToken"), // gerekiyorsa
                      filename: mediaModal.type === "pdf" ? "belge.pdf" : "gorsel.jpg",
                    })
                  }
                >
                  <ArrowDownTrayIcon width={18} height={18} />
                  İndir
                </button>
              </>
            ) : null
          }
        >
          {mediaModal.type === "pdf" ? (
            <iframe title="PDF" src={mediaModal.url} className="md-modal-iframe" />
          ) : (
            <img src={mediaModal.url} alt="Büyük görsel" className="md-modal-image" />
          )}
        </BasicModal>

        {/* Confirm submit modal (ortak BasicModal) */}
        <BasicModal
          open={confirmModal.open}
          title="Geri bildirim gönderilsin mi?"
          onClose={() => setConfirmModal({ open: false })}
          actions={
            <>
              <button type="button" className="btn-md btn-md-ghost" onClick={() => setConfirmModal({ open: false })}>
                Vazgeç
              </button>
              <button type="button" className="btn-md btn-md-primary" onClick={confirmSubmit} disabled={feedbackStatus === "loading"}>
                Evet, gönder
              </button>
            </>
          }
        >
          <div className="saha-confirm">
            <div className="saha-confirm-row">
              <ExclamationTriangleIcon width={18} height={18} />
              <div className="saha-wrap">Gönderim yaptıktan sonra değiştiremezsiniz. Bilgileri kontrol edip onaylayın.</div>
            </div>

            <div className="saha-confirm-sum">
              <KeyValueList
                items={[
                  { key: "Durum", value: STATUS_LABELS[fbStatus] || safeText(fbStatus) },
                 {
  key: "Açıklama",
  value: <span className="saha-wrap">{ensureFirstUpperTr(fbNote)}</span>,
},

                  { key: "Dosya", value: fbFiles.length ? `${fbFiles.length} görsel` : "—" },
                ]}
              />
            </div>
          </div>
        </BasicModal>
      </div>
    );
  };

  export default SahaPersoneliAssignmentDetailPage;
